/**
 * Query observation for the backend data boundary.
 *
 * Wraps a {@link DataClient} so every PostgREST request that executes is
 * reported to the query observer. Builder chains are observed by
 * {@link observeQueryBuilder}; `rpc()` is recorded here because it has no
 * builder. Results, errors, and rejections reach the caller untouched.
 */

import type { QueryObserver } from "@/lib/db/query-observer";
import { observeQueryBuilder, type QueryState, recordSafely } from "./observed-query-builder";
import type { DataClient, QueryBuilder, QueryResult, Row, TableName } from "./types";

/**
 * Returns a data client that reports executed queries to `observer`.
 */
export function observeDataClient(
  data: DataClient,
  observer: QueryObserver,
  clock: () => number = () => Date.now(),
): DataClient {
  return {
    from<T extends TableName>(table: T): QueryBuilder<Row<T>> {
      // Fresh state per `from()`; derived builders clone it to isolate the
      // recorded shape of each chain.
      const state: QueryState = {
        batched: false,
        filterColumns: [],
        operation: "select",
        paginated: false,
        table,
      };
      return observeQueryBuilder(data.from(table), state, observer, clock);
    },

    async rpc<TResult = unknown>(
      fn: string,
      params?: Record<string, unknown>,
      options?: { count?: "exact" | "planned" | "estimated"; head?: boolean },
    ): Promise<QueryResult<TResult>> {
      const startedAt = clock();
      const complete = recordSafely(observer, {
        batched: false,
        // Capture only code-defined argument names; values remain unobserved.
        filterColumns: Object.keys(params ?? {}).sort(),
        operation: "rpc",
        paginated: false,
        startedAt,
        table: fn,
      });
      try {
        return await data.rpc<TResult>(fn, params, options);
      } finally {
        complete(clock() - startedAt);
      }
    },
  };
}
